import { useLayoutEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { CHANCE_GAME_VENUES, CHANCE_GAMES_OFF_MESSAGE } from "@townsy/contracts";
import { useTownSession } from "../app/TownSessionContext";
import { selectIsGuest, useTownStore } from "../state/store";
import { VENUE_CLIENTS, VENUE_TITLES } from "./registry";
import { useVenueStore } from "./venueStore";

/**
 * The attraction's controls (ADR-0016) while the visitor is inside one of the four venues: a panel at the bottom of the
 * screen with the venue's own overlay, collapsible so it never hides the room. Shown only inside a venue room.
 * A guest's overlays offer "Sign up (18+) to play" instead of the betting controls (ADR-0048; the server enforces it).
 */
export function VenueHudHost() {
  const session = useTownSession();
  const { inside, chanceGamesEnabled, localActorId, guest } = useTownStore(
    useShallow((s) => ({
      inside: s.room.kind === "interior" && s.room.status === "joined",
      chanceGamesEnabled: Boolean(s.bootstrap?.features.venueChanceGamesEnabled),
      localActorId: s.localActorId,
      guest: selectIsGuest(s),
    })),
  );
  const v = useVenueStore(useShallow((s) => ({ venue: s.venue, shared: s.shared, mine: s.mine, chips: s.chips, events: s.events })));
  const [collapsed, setCollapsed] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const shown = Boolean(inside && v.venue && session);
  useVenueHudClearance(sectionRef, shown);
  if (!inside || !v.venue || !session) return null;
  const module = VENUE_CLIENTS[v.venue];
  const Hud = module.Hud;
  const Compact = module.Compact;
  const offNotice = !chanceGamesEnabled && CHANCE_GAME_VENUES.includes(v.venue);
  const hudProps = {
    venue: v.venue,
    shared: v.shared,
    mine: v.mine,
    chips: v.chips,
    chanceGamesEnabled,
    guest,
    send: (action: Record<string, unknown>) => session.sendVenueAction(action),
    localActorId,
    events: v.events,
  };
  return (
    <section ref={sectionRef} className={`venue-hud${collapsed ? " collapsed" : ""}`} aria-label={`${VENUE_TITLES[v.venue]} controls`} data-testid="venue-hud" data-venue={v.venue}>
      <header className="venue-hud-header">
        <strong>{VENUE_TITLES[v.venue]}</strong>
        {v.chips !== null ? (
          <span className="venue-chips" data-testid="venue-chips" title="Play chips: just for fun, no value">
            {v.chips} play chips
          </span>
        ) : null}
        <button type="button" className="btn btn-ghost btn-sm" aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)} data-testid="venue-hud-toggle">
          {collapsed ? "Show controls" : "Hide"}
        </button>
      </header>
      {collapsed ? (
        Compact ? (
          <Compact {...hudProps} />
        ) : null
      ) : (
        <>
          {offNotice ? (
            <p className="notice small" style={{ margin: 0 }} data-testid="venue-chance-off">
              {CHANCE_GAMES_OFF_MESSAGE}
            </p>
          ) : null}
          <Hud {...hudProps} />
        </>
      )}
    </section>
  );
}

/**
 * How much of the bottom of the screen the venue panel covers, as CSS variables on the root: `--venue-hud-clear` (from
 * the bottom of the screen to the panel's top) and `--venue-hud-clear-left` (the same when the panel reaches into the
 * left half, where the touch joystick and the Run button are). The bottom prompts and the touch controls sit above
 * the panel instead of underneath it (phones and tablets: the panel spans the whole width there).
 */
function useVenueHudClearance(ref: { current: HTMLElement | null }, shown: boolean): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    const clear = () => {
      root.style.removeProperty("--venue-hud-clear");
      root.style.removeProperty("--venue-hud-clear-left");
    };
    if (!shown || !el || typeof ResizeObserver === "undefined") {
      clear();
      return;
    }
    const apply = () => {
      const r = el.getBoundingClientRect();
      const fromBottom = Math.max(0, Math.ceil(window.innerHeight - r.top + 8));
      root.style.setProperty("--venue-hud-clear", `${fromBottom}px`);
      root.style.setProperty("--venue-hud-clear-left", r.left < window.innerWidth / 2 ? `${fromBottom}px` : "0px");
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
      clear();
    };
  }, [ref, shown]);
}
